import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook to handle PWA installation prompt
 * Returns installPrompt function and whether app is installable
 */
export const useInstallPrompt = () => {
  const [installState, setInstallState] = useState({
    deferredPrompt: null,
    isInstallable: false,
  });

  const isListenerAdded = useRef(false);

  useEffect(() => {
    // Prevent multiple listener additions in StrictMode
    if (isListenerAdded.current) return;

    // Check if app is already installed - do this check synchronously before adding listeners
    if (window.matchMedia('(display-mode: standalone)').matches) {
      console.log('PWA is already installed');
      return;
    }

    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();

      // Batch state updates to prevent multiple rerenders
      setInstallState({
        deferredPrompt: e,
        isInstallable: true,
      });

      console.log('PWA install prompt available');
    };

    const handleAppInstalled = () => {
      console.log('PWA was installed');
      // Batch state updates
      setInstallState({
        deferredPrompt: null,
        isInstallable: false,
      });
    };

    // Listen for the beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for app installed event
    window.addEventListener('appinstalled', handleAppInstalled);

    isListenerAdded.current = true;

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      isListenerAdded.current = false;
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!installState.deferredPrompt) {
      console.log('Install prompt not available');
      return false;
    }

    try {
      // Show the install prompt
      await installState.deferredPrompt.prompt();

      // Wait for the user to respond to the prompt
      const { outcome } = await installState.deferredPrompt.userChoice;

      console.log(`User response to install prompt: ${outcome}`);

      // Clear the deferred prompt - batch updates
      setInstallState({
        deferredPrompt: null,
        isInstallable: false,
      });

      return outcome === 'accepted';
    } catch (error) {
      console.error('Error showing install prompt:', error);
      return false;
    }
  }, [installState.deferredPrompt]);

  return {
    isInstallable: installState.isInstallable,
    promptInstall,
  };
};
